/**
 * npc_script_templates.ts — starter scripts for the Edit Mode NPC creator.
 *
 * Pure strings (no engine import) so the harness can lint them against the
 * ONE Lua API catalog: every `npc.*` call a template makes must be a
 * catalogued function, and every template must return the hook table.
 */

export interface NpcTemplate { key: string; label: string; hint: string; source: string }

const GREETER = `-- Greeter: notices you, waves, and chats about the place.
local met = false
return {
  on_init = function()
    npc.set_state("waiting")
    npc.set_sense(10)
    npc.set_greeting("Welcome, traveler. Ask me about this place.")
    npc.add_topic("place", "This place", "You're standing in " .. npc.world_name() .. ". Have a look around — I'll be right here.")
    npc.add_topic("weather", "The weather")
    npc.add_topic("time", "The time")
  end,
  on_player_enter = function(name, dist)
    npc.face_player()
    npc.emote("wave")
    if npc.player_met() then npc.say("Back again, " .. name .. "?") else npc.say("Hello there, " .. name .. "!") end
  end,
  on_player_leave = function(name)
    npc.say("Safe travels.", 2)
    npc.set_state("waiting")
  end,
  on_topic = function(key, name)
    if key == "weather" then npc.say("Looks like " .. npc.weather() .. " to me.") end
    if key == "time" then
      if npc.is_night() then npc.say("Late. Hour " .. npc.hour() .. " — you should rest.")
      else npc.say("It's hour " .. npc.hour() .. ". Plenty of day left.") end
    end
  end,
  on_say = function(name, text)
    npc.say("Hm, \\"" .. text .. "\\"? Couldn't say. Try one of my topics.")
  end,
  on_tick = function(dt)
    if npc.talking() then npc.face_player() end
  end,
}`;

const GUARD = `-- Guard: patrols four corners around its post, warns anyone who comes too close at night.
local corners = { {6, 6}, {-6, 6}, {-6, -6}, {6, -6} }
local i = 1
local function next_corner()
  i = i % #corners + 1
  npc.goto(npc.home_x() + corners[i][1], npc.home_z() + corners[i][2], 1.2)
  npc.set_state("patrolling")
end
return {
  on_init = function()
    npc.set_sense(8)
    npc.set_talkable(true)
    npc.set_greeting("Move along. Nothing to see here.")
    next_corner()
  end,
  on_arrive = function(x, z)
    npc.set_state("watching")
    -- pause a few seconds, then carry on
  end,
  on_tick = function(dt)
    if npc.state() == "watching" and npc.timer("pause", 4) then next_corner() end
    if npc.talking() then npc.stop(); npc.face_player() end
  end,
  on_player_enter = function(name, dist)
    npc.stop()
    npc.face_player()
    if npc.is_night() then npc.say("Halt! It's past curfew, " .. name .. ".") else npc.say("Evening. Keep to the road.") end
  end,
  on_player_leave = function(name)
    next_corner()
  end,
  on_talk_end = function(name)
    next_corner()
  end,
}`;

const QUESTGIVER = `-- Quest giver: remembers each player, hands out a task, checks it with a choice.
return {
  on_init = function()
    npc.set_state("worried")
    npc.set_greeting("You there — I've lost something. Will you help?")
  end,
  on_talk_start = function(name)
    npc.clear_topics()
    local stage = npc.memory_get("stage")
    if stage == "" then
      npc.add_topic("help", "I'll help")
      npc.add_topic("no", "Not now", "I understand. I'll be here.")
    elseif stage == "given" then
      npc.add_topic("done", "I found it!")
      npc.add_topic("hint", "Where should I look?", "Try near the tallest thing you can see. I dropped it coming down.")
    else
      npc.set_greeting("Thank you again, friend.")
    end
  end,
  on_topic = function(key, name)
    if key == "help" then
      npc.memory_set("stage", "given")
      npc.emote("nod")
      npc.say("Bless you. I dropped my lens somewhere out past the rocks. Bring it back and I'll remember it.")
      npc.set_state("hopeful")
      npc.clear_topics()
      npc.add_topic("hint", "Where should I look?", "Out past the rocks, toward the sunrise.")
    elseif key == "done" then
      npc.ask("Truly? Let me see it.", "Hand it over", "Just kidding")
    end
  end,
  on_choice = function(index, label, name)
    if index == 1 then
      npc.memory_set("stage", "done")
      npc.emote("bow")
      npc.say("My lens! You have my thanks, " .. name .. ". I won't forget this.")
      npc.set_state("grateful")
      npc.signal("quest_done", name)
    else
      npc.say("...that isn't funny.")
    end
  end,
}`;

const LIAISON = `-- Liaison: walks to the nearest versona (a living NPC) and asks it about its day.
local asked = false
return {
  on_init = function()
    npc.set_state("looking around")
    npc.set_greeting("I talk to the locals so you don't have to.")
  end,
  on_tick = function(dt)
    if asked then return end
    local v = npc.nearest_versona()
    if v == "" then return end
    if npc.versona_dist(v) > 3 then
      if not npc.moving() then npc.goto_versona(v); npc.set_state("walking over") end
    else
      npc.face(npc.versona_x(v), npc.versona_z(v))
      npc.set_state("chatting with " .. npc.versona_name(v))
      npc.versona_ask(v, "How is your day going, " .. npc.versona_name(v) .. "?")
      asked = true
    end
  end,
  on_versona_reply = function(id, text)
    npc.say(npc.versona_name(id) .. " says: " .. text, 8)
    npc.set_state("relaying")
    asked = false
    npc.timer("again", 1) -- arm; re-ask after a while
  end,
  on_player_enter = function(name, dist)
    local v = npc.nearest_versona()
    if v ~= "" and npc.versona_last(v) ~= "" then
      npc.say(npc.versona_name(v) .. " told me: " .. npc.versona_last(v), 6)
    end
  end,
}`;

const BLANK = `-- Blank NPC. Return a table of hooks; the npc.* API is listed in the panel.
return {
  on_init = function()
    npc.set_state("idle")
    npc.set_greeting("Yes?")
  end,
  on_tick = function(dt)
  end,
  on_interact = function(name, dist)
    npc.say("Hello, " .. name .. ".")
  end,
}`;

export const NPC_TEMPLATES: NpcTemplate[] = [
  { key: 'greeter', label: 'Greeter', hint: 'waves, topics, remembers you', source: GREETER },
  { key: 'guard', label: 'Patrol guard', hint: 'walks corners, curfew warning', source: GUARD },
  { key: 'quest', label: 'Quest giver', hint: 'stages in memory, a choice, a signal', source: QUESTGIVER },
  { key: 'liaison', label: 'Versona liaison', hint: 'asks a living NPC and relays it', source: LIAISON },
  { key: 'blank', label: 'Blank', hint: 'the minimal hook table', source: BLANK },
];

export function npcTemplate(key: string): string {
  return (NPC_TEMPLATES.find((t) => t.key === key) ?? NPC_TEMPLATES[0]).source;
}

/** Every `npc.<fn>` a script calls (for the catalog lint). */
export function npcCallsIn(source: string): string[] {
  const out = new Set<string>();
  for (const m of source.matchAll(/\bnpc\.([a-z_]+)\s*\(/g)) out.add(`npc.${m[1]}`);
  return [...out].sort();
}
